import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Loader2,
  Palette,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const PRESET_COLORS = [
  "#A5C8FF",
  "#A6E3A1",
  "#A4F1E4",
  "#F8C8DC",
  "#FFD8A8",
  "#E5C5FF",
  "#FFE49C",
  "#C7C7C7",
];

const emptyDraft = { id: null, name: "", color: "#A5C8FF", employee_codes: [] };

export default function DepartmentsPage() {
  const { t } = useTranslation();
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [expanded, setExpanded] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [newCode, setNewCode] = useState({});
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/departments");
      setDepts(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const startEdit = (d) => {
    setEditing(d.id);
    setDraft({
      id: d.id,
      name: d.name,
      color: d.color,
      employee_codes: d.employee_codes || [],
    });
  };
  const startNew = () => {
    setEditing("new");
    setDraft({ ...emptyDraft });
  };
  const cancelEdit = () => {
    setEditing(null);
    setDraft(emptyDraft);
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        const { data } = await api.post("/departments", {
          name: draft.name.trim(),
          color: draft.color,
          employee_codes: draft.employee_codes,
        });
        setDepts((curr) => [...curr, data]);
        toast.success(t("departments.created"));
      } else {
        const { data } = await api.patch(`/departments/${draft.id}`, {
          name: draft.name.trim(),
          color: draft.color,
          employee_codes: draft.employee_codes,
        });
        setDepts((curr) => curr.map((d) => (d.id === data.id ? data : d)));
        toast.success(t("departments.updated"));
      }
      cancelEdit();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/departments/${confirmDelete.id}`);
      setDepts((curr) => curr.filter((d) => d.id !== confirmDelete.id));
      toast.success(t("departments.deleted"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirmDelete(null);
    }
  };

  const addCode = async (deptId) => {
    const code = (newCode[deptId] || "").trim();
    if (!code) return;
    try {
      const { data } = await api.post(`/departments/${deptId}/employees`, {
        employee_code: code,
      });
      setDepts((curr) => curr.map((d) => (d.id === data.id ? data : d)));
      setNewCode({ ...newCode, [deptId]: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeCode = async (deptId, code) => {
    try {
      const { data } = await api.delete(
        `/departments/${deptId}/employees/${encodeURIComponent(code)}`
      );
      setDepts((curr) => curr.map((d) => (d.id === data.id ? data : d)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const renderEditingRow = () => (
    <TableRow data-testid="dept-edit-row" className="bg-accent/30">
      <TableCell>
        <Input
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="Departamento X"
          data-testid="dept-name-input"
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={draft.color}
            onChange={(e) => setDraft({ ...draft, color: e.target.value })}
            data-testid="dept-color-input"
            className="h-8 w-12 cursor-pointer rounded border border-border bg-transparent"
          />
          <div className="flex gap-0.5">
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setDraft({ ...draft, color: c })}
                className="h-5 w-5 rounded-full border border-border"
                style={{ backgroundColor: c }}
                aria-label={c}
              />
            ))}
          </div>
        </div>
      </TableCell>
      <TableCell className="font-mono text-xs">
        {draft.employee_codes.length}
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button size="sm" onClick={saveDraft} disabled={saving} data-testid="dept-save-btn">
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={cancelEdit}
            disabled={saving}
            data-testid="dept-cancel-btn"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1
            className="text-2xl font-semibold tracking-tight"
            data-testid="departments-page-title"
          >
            {t("departments.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("departments.subtitle")}
          </p>
        </div>
        <Button
          onClick={startNew}
          disabled={editing === "new"}
          data-testid="add-department-btn"
        >
          <Plus className="mr-2 h-4 w-4" /> {t("departments.addDepartment")}
        </Button>
      </div>

      <Card data-testid="departments-table-card">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex h-24 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="departments-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.name")}</TableHead>
                    <TableHead>{t("departments.color")}</TableHead>
                    <TableHead>{t("departments.employees")}</TableHead>
                    <TableHead>{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editing === "new" && renderEditingRow()}
                  {depts.length === 0 && editing !== "new" && (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-center text-sm text-muted-foreground"
                      >
                        {t("departments.noDepartments")}
                      </TableCell>
                    </TableRow>
                  )}
                  {depts.map((d) =>
                    editing === d.id ? (
                      <React.Fragment key={d.id}>{renderEditingRow()}</React.Fragment>
                    ) : (
                      <React.Fragment key={d.id}>
                        <TableRow data-testid={`dept-row-${d.id}`}>
                          <TableCell>
                            <button
                              type="button"
                              onClick={() =>
                                setExpanded((curr) => ({
                                  ...curr,
                                  [d.id]: !curr[d.id],
                                }))
                              }
                              className="flex items-center gap-1.5 font-medium"
                              data-testid={`expand-dept-${d.id}`}
                            >
                              {expanded[d.id] ? (
                                <ChevronDown className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5" />
                              )}
                              {d.name}
                            </button>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div
                                className="h-5 w-5 rounded-full border border-border"
                                style={{ backgroundColor: d.color }}
                              />
                              <span className="font-mono text-xs uppercase text-muted-foreground">
                                {d.color}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              data-testid={`dept-count-${d.id}`}
                              className="font-mono text-xs"
                            >
                              {d.employee_codes.length}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => startEdit(d)}
                                data-testid={`edit-dept-${d.id}`}
                                title={t("common.edit")}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => setConfirmDelete(d)}
                                data-testid={`delete-dept-${d.id}`}
                                title={t("common.delete")}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {expanded[d.id] && (
                          <TableRow data-testid={`dept-expanded-${d.id}`}>
                            <TableCell colSpan={4} className="bg-accent/20">
                              <div className="space-y-3 py-2">
                                <div className="flex flex-wrap gap-1.5">
                                  {d.employee_codes.length === 0 && (
                                    <span className="text-xs text-muted-foreground">
                                      {t("common.empty")}
                                    </span>
                                  )}
                                  {d.employee_codes.map((c) => (
                                    <Badge
                                      key={c}
                                      variant="outline"
                                      className="gap-1 font-mono text-xs"
                                      style={{ borderColor: d.color }}
                                    >
                                      {c}
                                      <button
                                        type="button"
                                        onClick={() => removeCode(d.id, c)}
                                        data-testid={`remove-code-${d.id}-${c}`}
                                        className="rounded-full p-0.5 hover:bg-destructive/20"
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </Badge>
                                  ))}
                                </div>
                                <div className="flex gap-2">
                                  <Input
                                    placeholder={t("departments.addEmployeeCode")}
                                    value={newCode[d.id] || ""}
                                    onChange={(e) =>
                                      setNewCode({
                                        ...newCode,
                                        [d.id]: e.target.value,
                                      })
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") addCode(d.id);
                                    }}
                                    data-testid={`add-code-input-${d.id}`}
                                    className="max-w-xs"
                                  />
                                  <Button
                                    size="sm"
                                    onClick={() => addCode(d.id)}
                                    data-testid={`add-code-btn-${d.id}`}
                                  >
                                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                                    {t("common.add")}
                                  </Button>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    )
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <DialogContent data-testid="delete-dept-dialog">
          <DialogHeader>
            <DialogTitle>{t("common.confirm")}</DialogTitle>
            <DialogDescription>
              {confirmDelete &&
                t("departments.confirmDelete", { name: confirmDelete.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={doDelete}
              data-testid="confirm-delete-dept-btn"
            >
              {t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
